<template>
  <div>
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title>
        Mustard Calendar
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-app-bar>
    <div class="ma-4" id="calendar-mustard">
      <validation-observer ref="form" v-slot="{ valid }">
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-row>
                <v-col cols="12" md="6">
                  <full-calendar
                    ref="fullcalendar"
                    :options="options"
                  ></full-calendar>
                  <small>Timezone: {{ calendarOptions.timeZone }}</small>
                  <div class="my-5" v-if="eventSelectedDetails.title">
                    <v-row no-gutters justify="space-between" align="center">
                      <v-col cols="4"><h4>Event Details</h4></v-col>
                      <v-col cols="6" class="text-right">
                        <small
                          ><a
                            v-if="!isEditing"
                            @click.prevent="populateSelectedEvent()"
                            class="d-inline-block text-decoration-none mx-3"
                            >Edit</a
                          >
                          <a
                            v-else
                            @click.prevent="cancelEditSelectedEvent()"
                            class="d-inline-block text-decoration-none mx-3"
                            >Cancel</a
                          ></small
                        >
                      </v-col>
                    </v-row>

                    <hr />
                    <h3 class="primary--text mt-5">
                      {{ eventSelectedDetails.title }}
                    </h3>
                    <p>
                      <small
                        >Date:
                        <strong
                          >{{ formatDate(eventSelectedDetails.start) }}
                          <span
                            v-if="
                              eventSelectedDetails.end &&
                                eventSelectedDetails.isRecurring
                            "
                            >- {{ formatDate(eventSelectedDetails.end) }}</span
                          ></strong
                        ></small
                      ><br />
                      <small
                        >Time:
                        <strong
                          ><span v-if="eventSelectedDetails.allDay"
                            >All day</span
                          ><span v-else
                            >{{
                              formatTime(
                                new Date(
                                  eventSelectedDetails.startRecur ||
                                    eventSelectedDetails.start
                                ).getTime()
                              )
                            }}
                            -
                            {{
                              formatTime(
                                new Date(
                                  eventSelectedDetails.endRecur ||
                                    eventSelectedDetails.end
                                ).getTime()
                              )
                            }}</span
                          ></strong
                        ></small
                      ><br />
                      <span v-if="eventSelectedDetails.isRecurring">
                        <small
                          >Every:
                          <strong
                            v-if="
                              eventSelectedDetails.extendedProps.repeat_by ===
                                `daily`
                            "
                            >Daily</strong
                          >
                          <strong
                            v-else
                            v-for="(repeat, i) in eventSelectedDetails.repeats"
                            :key="`repeat-` + i"
                            >&nbsp;{{ repeat
                            }}<span
                              v-if="
                                i <= eventSelectedDetails.repeats.length - 2
                              "
                              >,</span
                            >
                          </strong></small
                        >
                      </span>
                    </p>

                    <p>
                      {{ eventSelectedDetails.extendedProps.description }}
                    </p>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card elevation="0" class="px-10">
                    <v-card-title class="subtitle-1"
                      >{{ isEditing ? `Edit` : `Create` }} Event</v-card-title
                    >
                    <v-form class="mt-5">
                      <validation-provider
                        rules="required|min:5"
                        name="title"
                        v-slot="{ errors }"
                      >
                        <v-text-field
                          outlined
                          required
                          clearable
                          label="Title"
                          placeholder="Event name"
                          v-model="formEvent.values.name"
                          :error-messages="errors"
                        ></v-text-field>
                      </validation-provider>
                      <validation-provider name="description">
                        <v-textarea
                          outlined
                          required
                          clearable
                          auto-grow
                          no-resize
                          counter="500"
                          maxlength="500"
                          label="Description"
                          placeholder="Brief description"
                          class="mt-5"
                          v-model="formEvent.values.description"
                        ></v-textarea>
                      </validation-provider>

                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-checkbox
                            label="Recurring"
                            v-model="formEvent.values.is_recurring"
                          ></v-checkbox>
                        </v-col>
                        <v-col cols="6">
                          <v-checkbox
                            label="All day"
                            v-model="formEvent.values.is_all_day"
                          ></v-checkbox>
                        </v-col>
                      </v-row>

                      <v-row v-if="formEvent.values.is_recurring">
                        <v-col cols="12">
                          <validation-provider
                            rules="required"
                            name="repeatby"
                            v-slot="{ errors }"
                          >
                            <v-radio-group
                              v-model="recurring.repeatsBy"
                              :error-messages="errors"
                              row
                            >
                              <v-radio
                                class="text-capitalize"
                                v-for="(repeat, i) in recurring.repeats"
                                :key="i"
                                :label="repeat"
                                :value="repeat"
                              ></v-radio>
                            </v-radio-group>
                          </validation-provider>
                        </v-col>
                      </v-row>

                      <v-row
                        no-gutters
                        class="mb-5"
                        v-if="
                          recurring.repeatsBy === `weekly` &&
                            formEvent.values.is_recurring
                        "
                      >
                        <v-col cols="12" class="weekdays mb-5">
                          <validation-provider rules="required" name="weekday">
                            <v-checkbox
                              class="d-inline-flex mt-0"
                              v-model="recurring.selectedWeekdays"
                              v-for="(weekday, i) in recurring.weekdays"
                              :key="i"
                              :label="weekday"
                              :value="i"
                              hide-details
                              dense
                            ></v-checkbox>
                          </validation-provider>
                        </v-col>
                      </v-row>

                      <v-row class="mt-5">
                        <v-col
                          :cols="formEvent.values.is_recurring ? `6` : `12`"
                        >
                          <datepicker
                            :ref-name="date.start.name"
                            :value="date.start.value"
                            :label="date.start.label"
                            :min-date="minStartDate"
                            @update:date="updateDate($event, date.start)"
                          ></datepicker>
                        </v-col>
                        <v-col cols="6" v-if="formEvent.values.is_recurring">
                          <datepicker
                            :ref-name="date.end.name"
                            :value="date.end.value"
                            :label="date.end.label"
                            :min-date="date.end.min"
                            @update:date="updateDate($event, date.end)"
                          ></datepicker>
                        </v-col>
                        <v-col cols="6" v-if="!formEvent.values.is_all_day">
                          <timepicker
                            :ref-name="time.start.name"
                            :value="time.start.value"
                            :label="time.start.label"
                            :min-time="time.start.min"
                            @update:time="updateTime($event, time.start)"
                          ></timepicker>
                        </v-col>
                        <v-col cols="6" v-if="!formEvent.values.is_all_day">
                          <timepicker
                            :ref-name="time.end.name"
                            :value="time.end.value"
                            :label="time.end.label"
                            :min-time="time.end.min"
                            @update:time="updateTime($event, time.end)"
                          ></timepicker>
                        </v-col>
                      </v-row>

                      <v-row no-gutters>
                        <v-col cols="6">
                          <v-btn
                            color="primary"
                            :loading="isSubmitting"
                            :disabled="!valid || isSubmitting"
                            @click.prevent="
                              isEditing ? updateSelectedEvent() : addEvent()
                            "
                            >{{ isEditing ? `Save Changes` : `Add Event` }}
                          </v-btn>
                        </v-col>
                        <v-col
                          cols="6"
                          class="text-right"
                          align-self="center"
                          v-if="isEditing"
                        >
                          <small>
                            <a
                              @click.prevent="
                                confirmDeleteEvent(eventSelectedDetails.id)
                              "
                              class="d-inline-block text-decoration-none mx-3 error--text"
                              >Delete</a
                            >
                          </small>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-card>
                </v-col>
              </v-row>
              <v-overlay absolute :value="isLoading" opacity="0.1">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="64"
                ></v-progress-circular>
              </v-overlay>
              <v-snackbar
                v-model="snackbar.show"
                :color="snackbar.status"
                :vertical="true"
                right
              >
                {{ snackbar.message }}
                <template v-slot:action="{ attrs }">
                  <v-btn
                    text
                    color="white"
                    v-bind="attrs"
                    @click="snackbar.show = false"
                  >
                    Ok
                  </v-btn>
                </template>
              </v-snackbar>
            </v-col>
          </v-row>
        </v-container>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import FullCalendar from '@fullcalendar/vue'
import Datepicker from '@/components/widgets/Datepicker'
import Timepicker from '@/components/widgets/Timepicker'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import dayjs from 'dayjs'
import { mapState, mapMutations, mapActions } from 'vuex'

export default {
  name: 'Calendar',
  components: {
    AppBarNavIcon,
    FullCalendar,
    Datepicker,
    Timepicker
  },
  data() {
    return {
      isEditing: false,
      isLoading: false,
      isSubmitting: false,
      snackbar: {
        show: false,
        message: null,
        status: ''
      },
      date: {
        start: {
          value: '',
          label: 'Date',
          name: 'startdate'
        },
        end: {
          value: '',
          label: 'End Date',
          name: 'enddate',
          min: undefined
        }
      },
      time: {
        start: {
          value: '',
          label: 'Start Time',
          name: 'starttime',
          min: undefined
        },
        end: {
          value: '',
          label: 'End Time',
          name: 'endtime',
          min: undefined
        }
      },
      recurring: {
        repeats: ['daily', 'weekly'],
        repeatsBy: '',
        weekdays: [],
        selectedWeekdays: []
      },
      formEvent: {
        defaults: {
          name: '',
          description: '',
          start_at: '',
          end_at: '',
          is_all_day: false,
          is_recurring: false
        },
        values: {}
      },
      eventSelected: {},
      options: {
        plugins: [dayGridPlugin, interactionPlugin],
        eventClick: async event => {
          this.isLoading = true
          await this.cancelEditSelectedEvent()
          this.eventSelected.id = parseInt(event.event._def.publicId)
          await this.getEvent(this.eventSelected.id)
          this.isLoading = !this.eventSelectedDetails.title
        }
      }
    }
  },
  async mounted() {
    this.isLoading = true
    await this.setTimezone(dayjs.tz.guess())
    await this.refreshCalendar()
    await this.clearFields()
    this.isLoading = false
  },
  watch: {
    time: {
      deep: true,
      handler() {
        this.updateEndTime()
        this.updateFormEventDate()
      }
    },
    date: {
      deep: true,
      handler() {
        this.updateEndDate()
        this.updateEndTime()
        this.updateFormEventDate()
      }
    },
    'formEvent.values.is_recurring'(n) {
      this.date.start.label = n ? 'Start Date' : 'Date'
      this.updateEndDate()
      this.updateFormEventDate()

      if (n) {
        this.formEvent.values.repeat_by = this.recurring.repeatsBy
        this.formEvent.values.days_of_week = this.recurring.selectedWeekdays
      } else {
        delete this.formEvent.values.repeat_by
        delete this.formEvent.values.days_of_week
      }
    },
    'formEvent.values.is_all_day'(n) {
      this.updateFormEventDate()
    },
    'recurring.selectedWeekdays'(n) {
      this.formEvent.values.days_of_week = n
    },
    'recurring.repeatsBy'(n) {
      this.formEvent.values.repeat_by = n
      this.formEvent.values.days_of_week = []

      if (n === 'daily') {
        this.recurring.weekdays.forEach((day, key) => {
          this.formEvent.values.days_of_week.push(key)
        })
      } else {
        this.formEvent.values.days_of_week = this.recurring.selectedWeekdays
      }
    }
  },
  computed: {
    ...mapState({
      calendarOptions: state => state.calendar.options,
      httpError: state => state.calendar.httpError,
      eventSelectedDetails: state => state.calendar.eventSelectedDetails
    }),
    minStartDate() {
      return dayjs().format('YYYY-MM-DD')
    }
  },
  methods: {
    ...mapActions({
      getEvent: 'calendar/getEvent',
      getEvents: 'calendar/getEvents',
      postEvent: 'calendar/postEvent',
      updateEvent: 'calendar/updateEvent',
      deleteEvent: 'calendar/deleteEvent'
    }),
    ...mapMutations({
      setTimezone: 'calendar/setTimezone',
      clearEventSelectedDetails: 'calendar/clearEventSelectedDetails'
    }),

    updateDate(value, date) {
      date.value = value
    },
    updateTime(value, time) {
      time.value = value
    },
    updateEndDate() {
      let startDayIsMoreThanEndDay =
        dayjs(this.date.start.value) >= dayjs(this.date.end.value)
      let startDayIsSameAsEndDay =
        dayjs(this.date.start.value).startOf('d').valueOf ===
        dayjs(this.date.end.value).startOf('d').valueOf

      if (
        startDayIsMoreThanEndDay &&
        startDayIsSameAsEndDay &&
        this.formEvent.values.is_recurring
      ) {
        this.date.end.value = dayjs(this.date.start.value)
          .add(1, 'd')
          .format('YYYY-MM-DD')
        this.date.end.min = this.date.end.value
      } else {
        this.date.end.min = this.date.start.value
      }
    },
    async updateEndTime() {
      let selectedDay = dayjs(this.date.start.value)
        .startOf('d')
        .valueOf()
      let today = dayjs()
        .startOf('d')
        .valueOf()
      let startTime = dayjs(this.time.start.value, 'HH:mm').valueOf()
      let endTime = dayjs(this.time.end.value, 'HH:mm').valueOf()
      if (today === selectedDay) {
        this.time.start.min = dayjs().format('HH:mm')
      } else {
        this.time.start.min = undefined
      }
      if (startTime > endTime) {
        this.time.end.value = dayjs(this.time.start.value, 'HH:mm')
          .add(5, 'm')
          .format('HH:mm')
      }
      this.time.end.min = dayjs(this.time.start.value, 'HH:mm')
        .add(5, 'm')
        .format('HH:mm')
    },
    updateFormEventDate() {
      let timeStart = dayjs(this.time.start.value, 'HH:mm')
      let dateStart = dayjs(this.date.start.value)
        .tz(this.calendarOptions.timeZone)
        .format()
      let timeEnd = dayjs(this.time.end.value, 'HH:mm')
      let dateEnd = dayjs(this.date.end.value)
        .tz(this.calendarOptions.timeZone)
        .format()

      if (!this.formEvent.values.is_all_day) {
        dateStart = dayjs(dateStart)
          .hour(timeStart.$H)
          .minute(timeStart.$m)
          .valueOf()

        dateEnd = dayjs(dateEnd)
          .hour(timeEnd.$H)
          .minute(timeEnd.$m)
          .valueOf()
      }

      this.formEvent.values.start_at = dayjs(dateStart)
        .utc()
        .format()
      this.formEvent.values.end_at = dayjs(dateEnd)
        .utc()
        .format()

      if (
        !this.formEvent.values.is_recurring &&
        this.formEvent.values.is_all_day
      ) {
        this.formEvent.values.end_at = this.formEvent.values.start_at
      }
    },
    setTimeInit() {
      let currMins = dayjs().minute()
      let setHours = dayjs()
      let setCurrMins = null

      switch (true) {
        case currMins >= 0 && currMins <= 5:
          setCurrMins = '5'
          break
        case currMins >= 6 && currMins <= 10:
          setCurrMins = '10'
          break
        case currMins >= 11 && currMins <= 15:
          setCurrMins = '15'
          break
        case currMins >= 16 && currMins <= 20:
          setCurrMins = '20'
          break
        case currMins >= 21 && currMins <= 25:
          setCurrMins = '25'
          break
        case currMins >= 26 && currMins <= 30:
          setCurrMins = '30'
          break
        case currMins >= 31 && currMins <= 35:
          setCurrMins = '35'
          break
        case currMins >= 36 && currMins <= 40:
          setCurrMins = '40'
          break
        case currMins >= 41 && currMins <= 45:
          setCurrMins = '45'
          break
        case currMins >= 46 && currMins <= 50:
          setCurrMins = '50'
          break
        case currMins >= 51 && currMins <= 55:
          setCurrMins = '55'
          break
        case currMins >= 56 && currMins <= 59:
          setCurrMins = '00'
          setHours = setHours.add(1, 'h')
          break
      }

      let currTime = setHours.minute(parseInt(setCurrMins)).valueOf()

      this.time.start.value = dayjs(currTime).format('HH:mm')
      this.time.end.value = dayjs(currTime)
        .add(5, 'm')
        .format('HH:mm')
    },
    clearFields() {
      this.formEvent.values = Object.assign({}, this.formEvent.defaults)
      this.date.start.value = this.date.end.value = this.minStartDate
      this.recurring.weekdays = dayjs.weekdaysShort()
      this.recurring.selectedWeekdays = []
      this.setTimeInit()
    },
    addEvent() {
      this.isSubmitting = true
      this.postEvent(this.formEvent.values)
        .then(async () => {
          await this.refreshCalendar()
          this.snackbar.show = true
          this.snackbar.status = 'success'
          this.snackbar.message = 'Added the event successfully'
          this.clearFields()
          await this.$refs.form.reset()
        })
        .catch(error => {
          if (error.status) {
            this.snackbar.show = true
            this.snackbar.status = 'error'
            this.snackbar.message = this.httpError.message
          }
          this.$refs.form.setErrors(error)
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    async refreshCalendar() {
      await this.getEvents().then(() => {
        this.options = { ...this.calendarOptions, ...this.options }
        this.options.events = this.calendarOptions.events
      })
    },
    formatDate(date) {
      return dayjs(date)
        .tz(this.calendarOptions.timeZone)
        .format('MMMM D')
    },
    formatTime(time) {
      return dayjs(time)
        .tz(this.calendarOptions.timeZone)
        .format('h:mm A')
    },
    updateSelectedEvent() {
      this.isSubmitting = true
      this.updateEvent(this.formEvent.values)
        .then(async () => {
          this.snackbar.show = true
          this.snackbar.status = 'success'
          this.snackbar.message = 'Edited the event successfully'

          this.refreshCalendar()
          this.clearFields()
          this.clearEventSelectedDetails()
          await this.$refs.form.reset()
        })
        .catch(error => {
          if (error.status) {
            this.snackbar.show = true
            this.snackbar.status = 'error'
            this.snackbar.message = this.httpError.message
          }
          this.$refs.form.setErrors(error)
        })
        .finally(() => {
          this.isEditing = false
          this.isSubmitting = false
        })
    },
    confirmDeleteEvent(eventId) {
      this.$confirm(`Are you sure you want to delete the selected event?`, {
        color: 'primary',
        title: 'Delete Event'
      }).then(confirm => {
        if (confirm) {
          this.deleteSelectedEvent(eventId)
        }
      })
    },
    deleteSelectedEvent(eventId) {
      this.isSubmitting = true
      this.deleteEvent(eventId)
        .then(async () => {
          this.snackbar.show = true
          this.snackbar.status = 'success'
          this.snackbar.message = 'Event deleted'

          this.clearFields()
          this.refreshCalendar()
          this.clearEventSelectedDetails()
          await this.$refs.form.reset()
        })
        .catch(error => {
          if (error.status) {
            this.snackbar.show = true
            this.snackbar.status = 'error'
            this.snackbar.message = this.httpError.message
          }
          this.$refs.form.setErrors(error)
        })
        .finally(() => {
          this.isEditing = false
          this.isSubmitting = false
        })
    },
    populateSelectedEvent() {
      this.isEditing = true
      this.formEvent.values.id = this.eventSelectedDetails.id
      this.formEvent.values.is_recurring = this.eventSelectedDetails.isRecurring
      this.formEvent.values.is_all_day = !!this.eventSelectedDetails.allDay

      this.formEvent.values.name = this.eventSelectedDetails.title
      this.formEvent.values.description = this.eventSelectedDetails.extendedProps.description

      this.date.start.value = dayjs(this.eventSelectedDetails.start).format(
        'YYYY-MM-DD'
      )
      this.date.end.value = dayjs(this.eventSelectedDetails.end).format(
        'YYYY-MM-DD'
      )
      this.time.start.value = dayjs(this.eventSelectedDetails.start).format(
        'HH:mm'
      )
      this.time.end.value = dayjs(this.eventSelectedDetails.end).format('HH:mm')

      this.recurring.repeatsBy = this.formEvent.values.repeat_by = this.eventSelectedDetails.extendedProps.repeat_by
      this.recurring.selectedWeekdays = this.formEvent.values.days_of_week = this
        .eventSelectedDetails.daysOfWeek
        ? this.eventSelectedDetails.daysOfWeek
        : this.eventSelectedDetails.extendedProps.days_of_week
    },
    cancelEditSelectedEvent() {
      this.isEditing = false
      this.clearFields()
    }
  }
}
</script>

<style lang="sass">
#calendar-mustard
  .col
    padding-top: 0
  .v-card__title.subtitle-1
      padding: 0 !important
  .weekdays
    .v-input--checkbox
      width: 80px
</style>
